<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('order.hafciarnia.ordersInHafciarnia') }}</span>
        <h3 class="page-title">{{ $t('order.hafciarnia.listOfOordersInHafciarnia') }}</h3>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button theme="success"
          @click.prevent="$router.push('/zamowienia/nowe')">
          <i class="material-icons">add</i>
          {{ $t('order.addNewOrder') }}
        </d-button>
      </d-col>
    </d-row>

    <d-row
      style="width: 100%;"
      class="d-flex ml-0 mb-3 page-filters">
      <validation-observer
        ref="orders-filters-form"
        tag="form"
        style="width: 100%;"
        class="d-flex orders-filter flex-wrap">
        <div class="d-flex mb-2 w-100">
          <d-input
            v-model="filters.order_name"
            :placeholder="$t('ui.orderName')"
            class="w-50" />

          <date-range-picker
            ref="picker"
            :locale-data="{
              'firstDay': 1,
              'format': 'DD-MM-YYYY',
              'daysOfWeek': [$t('ui.daysOfWeek.sun'), $t('ui.daysOfWeek.mon'), $t('ui.daysOfWeek.tue'), $t('ui.daysOfWeek.wed'), $t('ui.daysOfWeek.thur'), $t('ui.daysOfWeek.fri'), $t('ui.daysOfWeek.sat')],
              'monthNames': [$t('ui.monthNames.jan'), $t('ui.monthNames.feb'), $t('ui.monthNames.mar'), $t('ui.monthNames.apr'), $t('ui.monthNames.may'), $t('ui.monthNames.jun'), $t('ui.monthNames.jul'), $t('ui.monthNames.aug'), $t('ui.monthNames.sep'), $t('ui.monthNames.oct'), $t('ui.monthNames.nov'), $t('ui.monthNames.dec')],
            }"
            class="ml-2 w-50"
            style="min-width: 200px;"
            :singleDatePicker="false"
            :timePicker="false"
            :showWeekNumbers="false"
            :showDropdowns="false"
            :autoApply="true"
            v-model="dateRange"
            :ranges="false"
            :linkedCalendars="true">
            <template v-slot:input="picker" style="min-width: 350px;">
              {{ formatDateStart(picker.startDate) }} - {{ formatDateEnd(picker.endDate) }}
            </template>
          </date-range-picker>

          <v-select
            v-if="!userIsWarehouseman"
            class="ml-2 w-50"
            :options="orderStatusOptions"
            v-model="filters.status"
            :placeholder="$t('status.statusQuestionMark')"
            :searchable="false"
            label="text">
          </v-select>

          <select-customer
            v-if="!userIsWarehouseman && !userIsCustomer"
            :customerID="filters.customer_id"
            class="w-50 ml-2" />
        </div>

        <div class="d-flex mb-2 w-100">
          <v-select
            v-if="!userIsWarehouseman && !userIsCustomer"
            class="w-25"
            :options="orderWorkerOptions"
            v-model="filters.user_id"
            :placeholder="$t('order.isWorker')"
            :searchable="false"
            label="text">
          </v-select>

          <v-select
            class="ml-2 mr-2 w-50"
            :options="productCodesOptions"
            v-model="filters.productCode"
            ref="product-codes-filter"
            :placeholder="$t('product.productsCodes')"
            :searchable="true"
            :multiple="true"
            label="text"
            :filter="filteredProductCodes">
            <template slot="no-options">
              <template v-if="$refs['product-codes-filter'] && $refs['product-codes-filter'].search.length">
                {{ $t('product.noProductsMatchingGivenPhrase') }}&hellip;
              </template>
              <template v-else>
                {{ $t('product.noProductsMatchingGivenPhrase') }}
              </template>
            </template>
          </v-select>

          <v-select
            class="ml-2 mr-2 w-50"
            :options="productTagsOptions"
            v-model="filters.productTags"
            :placeholder="$t('order.productsTags')"
            :searchable="true"
            :multiple="true"
            label="text">
            <template slot="no-options">
              {{ $t('order.noTagsMatchingGivenPhrase') }}&hellip;
            </template>
          </v-select>

          <d-button
            theme="info"
            outline
            class="text-nowrap ml-auto"
            @click.prevent="validateFiltersForm">{{ $t('ui.search') }}</d-button>

          <d-button
            theme="danger"
            outline
            class="text-nowrap ml-2"
            @click.prevent="clearFilters">{{ $t('ui.resetFilters') }}</d-button>
        </div>
      </validation-observer>
    </d-row>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0">{{ $t('ui.order') }}</th>
            <th scope="col" class="border-0 text-center">{{ $t('order.hafciarnia.dateOfTransferToHafciarnia') }}</th>
            <th scope="col" class="border-0 text-center">{{ $t('order.hafciarnia.syncDate') }}</th>
            <th scope="col" class="border-0 text-center">{{ $t('ui.actions') }}</th>
          </tr>
        </thead>
        <tbody v-if="allDataLoaded">
          <tr
            v-for="(order, index) of orders"
            :key="'order-' + index">
            <td class="align-middle">
              {{ order.order_name }}<br>
              <a
                v-if="order.embriodery_id"
                :href="hafciarniaUrl + 'zamowienia/edycja/' + parseInt(order.embriodery_id, 10)"
                target="_blank"
                rel="noopener noreferrer">
                {{ $t('order.hafciarnia.orderInHafciarnia') }}
              </a>
            </td>
            <td class="align-middle text-center">
              {{ order.embriodery_sent_at || '-' }}
            </td>
            <td class="align-middle text-center">
              <template v-if="!!order.embriodery_sent_at && !!order.embriodery_synchronized_at">
                <d-badge theme="success" :outline="true">
                  {{ $t('order.hafciarnia.synced') }}
                </d-badge><br>
                <small class="mt-2 d-block">{{ order.embriodery_synchronized_at }}</small>
              </template>
              <template v-else-if="!!order.embriodery_sent_at && !order.embriodery_synchronized_at">
                <d-badge theme="warning" :outline="true">
                  {{ $t('order.hafciarnia.syncInProgress') }}
                </d-badge>
              </template>
              <template v-else>
                <d-badge theme="danger" :outline="true">
                  {{ $t('order.hafciarnia.notSynced') }}
                </d-badge>
              </template>
            </td>
            <td
              class="align-middle text-center"
              style="width: 80px;">
              <d-button
                theme="primary"
                @click.native="$router.push('/zamowienia-hafciarnia/edycja/' + order.id)"
                class="mx-1"
                :title="$t('ui.edit')">
                <i class="material-icons">edit</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="orders.length === 0">
            <td
              colspan="5"
              class="text-center">
              {{ $t('purchase.noOrdersToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />
  </d-container>
</template>

<script>
import AppConfig from '@/.env';
import CustomerPanel from './../mixins/CustomerPanel.vue';
import ListUtils from '@/utils/ListUtils.js';
import FormUtils from '@/utils/FormUtils.js';
import HasFilters from '@/mixins/HasFilters.vue';
import HasPagination from '@/mixins/HasPagination.vue';
import Pagination from '@/components/common/Pagination.vue';
import SelectCustomer from '@/components/common/SelectCustomer.vue';
import debounce from 'lodash.debounce';
import DateRangePicker from 'vue2-daterange-picker';
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css';

export default {
  name: 'orders-hafciarnia',
  mixins: [
    CustomerPanel,
    HasPagination,
    HasFilters
  ],
  components: {
    'select-customer': SelectCustomer,
    'date-range-picker': DateRangePicker,
    'pagination': Pagination
  },
  computed: {
    allDataLoaded () {
      return this.loaded && this.loadedWorkers && this.loadedProducts && this.loadedProductTags;
    },
    userIsWarehouseman () {
      return this.$store.getters.getUserType === 'warehouse_manager';
    },
    userIsCustomer () {
      return this.$store.getters.getUserType === 'customer';
    },
    hafciarniaUrl () {
      let environment = AppConfig.useConfig;
      return AppConfig.additionalConfig[environment].hafciarniaUrl;
    },
    productCodesOptions () {
      let options = [];

      if (this.$refs['product-codes-filter'] && this.$refs['product-codes-filter'].search.length < 1) {
        return options;
      }

      for (let i = 0; i < this.products.length; i++) {
        let option = {
          text: this.products[i].code,
          value: '[#' + this.products[i].id + '#]' + this.products[i].code
        };

        options.push(option);
      }

      return options;
    },
    orderWorkerOptions () {
      return this.workers.map(worker => ({
        text: worker.name,
        value: worker.id
      }));
    },
    orderStatusOptions () {
      return [
        {
          text: this.$t('order.toCollect'),
          value: 'TO_COLLECT'
        },
        {
          text: this.$t('order.waitingPurchaseProducts'),
          value: 'WAITING_PURCHASE_PRODUCTS'
        },
        {
          text: this.$t('ui.order'),
          value: 'ORDER'
        },
        {
          text: this.$t('order.inProduction'),
          value: 'IN_PRODUCTION'
        },
        {
          text: this.$t('order.toCheckInWarehouse'),
          value: 'TO_CHECK'
        },
        {
          text: this.$t('order.readyForShipping'),
          value: 'PREPARATION_FOR_SHIPPING'
        },
        {
          text: this.$t('order.waitingForShipping'),
          value: 'WAITING_FOR_SHIPPING'
        },
        {
          text: this.$t('order.sent'),
          value: 'SENT'
        },
        {
          text: this.$t('order.pendingPayment'),
          value: 'PENDING_PAYMENT'
        },
        {
          text: this.$t('order.ended'),
          value: 'ENDED'
        },
        {
          text: this.$t('order.toDelete'),
          value: 'TO_DELETE'
        },
        {
          text: this.$t('order.deleted'),
          value: 'DELETED'
        }
      ];
    },
    productTagsOptions () {
      let options = [];

      for (let i = 0; i < this.productTags.length; i++) {
        for (let j = 0; j < this.productTags[i].tags.length; j++) {
          let tagName = this.productTags[i].tags[j].name;
          let tagID = this.productTags[i].tags[j].id;
          let tagCategoryID = this.productTags[i].tags[j].category_id;
          let tagCategoryName = this.productTags[i].name;

          let option = {
            text: tagCategoryName + ': ' + tagName,
            value: tagCategoryID + ';' + tagID
          };

          options.push(option);
        }
      }

      return options;
    },
  },
  data() {
    return {
      orders: [],
      loaded: false,
      loadError: false,
      removeInProgress: false,
      dateRange: {
        startDate: null,
        endDate: null
      },
      filters: {
        order_name: '',
        status: null,
        customer_id: null,
        user_id: null,
        productCode: null,
        productTags: null,
      },
      workers: [],
      loadedWorkers: false,
      products: [],
      loadedProducts: false,
      loadedProductTags: false,
      productTags: []
    };
  },
  mounted () {
    this.$bus.$on('ui-select-customer-updated', this.setCustomerID);
    this.loadFilteredData();
    this.loadAdditionalData();
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = {
        in_embriodery: 1
      };

      if (this.filters.order_name && this.filters.order_name !== '') {
        where.order_name = this.filters.order_name;
      }

      if (this.dateRange.startDate) {
        where.created_at_from = this.prepareDateString(this.dateRange.startDate);
      }

      if (this.dateRange.endDate) {
        where.created_at_to = this.prepareDateString(this.dateRange.endDate);
      }

      if (this.filters.status !== null && this.filters.status !== undefined) {
        where.status = this.filters.status.value;
      } else {
        where.excluded_status = ['INQUIRY', 'NEW', 'SENT_TO_CUSTOMER'];
      }

      if (this.filters.customer_id && this.filters.customer_id > 0) {
        where.customer_id = this.filters.customer_id;
      }

      if (this.filters.user_id) {
        where.user_id = this.filters.user_id.value;
      }

      if (this.filters.productCode) {
        if (!where['products']) {
          where.products = {
            code: []
          };
        }

        where['products']['code'] = this.prepareProductCodesFilter(this.filters.productCode);
      }


      if (this.filters.productTags) {
        if (!where['products']) {
          where.products = {
            tags: []
          };
        }

        where['products']['tags'] = this.prepareProductTagsFilter(this.filters.productTags);
      }

      ListUtils.loadPaginatedItemsData(this, {
        endpoint: '/orders/items?page=' + this.currentPage,
        listField: 'orders',
        method: 'get',
        params: {
          where
        }
      });
    },
    loadAdditionalData () {
      if (!this.userIsCustomer) {
        FormUtils.loadAdditionalData(this, {
          endpoint: this.getApiEndpoint('/users/items'),
          loadedKey: 'loadedWorkers',
          params: {
            return: 'get',
            where: {
              type: ['worker', 'admin']
            }
          },
          outputKey: 'workers',
          errorKey: 'loadError'
        });

        FormUtils.loadAdditionalData(this, {
          endpoint: this.getApiEndpoint('/tags/categories/items'),
          loadedKey: 'loadedProductTags',
          params: {
            with: ['tags']
          },
          outputKey: 'productTags',
          errorKey: 'loadError'
        });
      }

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: this.getApiEndpoint('/products/all-items-id-code'),
        loadedKey: 'loadedProducts',
        outputKey: 'products',
        errorKey: 'loadError'
      });
    },
    validateFiltersForm () {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(this.$refs['orders-filters-form'], this.debouncedLoadFilteredData);
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        order_name: '',
        status: null,
        customer_id: null,
        user_id: null,
        productCode: null,
        productTags: null,
      };
      this.dateRange = {
        startDate: null,
        endDate: null,
        customer_id: null,
      };

      this.$bus.$emit('ui-select-customer-reset');
      this.$bus.$emit('view-filters-reset');
      this.$bus.$emit('pagination-reset', false);
    },
    formatDateStart (input) {
      if (input === null) {
        return this.$t('ui.searchFrom');
      }

      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    formatDateEnd (input) {
      if (input === null) {
        return this.$t('ui.searchTo');
      }

      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    prepareDateString (input) {
      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    prepareProductCodesFilter (productCode) {
      let output = [];

      for (let i = 0; i < productCode.length; i++) {
        output.push(productCode[i].value.replace(/\[\#[0-9]{1,}\#\]/gmi, ''));
      }

      return output;
    },
    filterByProductCodes (option, label, search) {
      return (option.text).toLowerCase().indexOf(search.toLowerCase()) > -1;
    },
    filteredProductCodes (options, search) {
      return options.filter(option => this.filterByProductCodes(option, option.id, search)).slice(0, 50);
    },
    prepareProductTagsFilter (productTags) {
      let output = {};

      for (let i = 0; i < productTags.length; i++) {
        let tagData = productTags[i].value.split(';');
        let tagCategoryID = parseInt(tagData[0], 10);
        let tagID = parseInt(tagData[1], 10);

        if (output[tagCategoryID]) {
          output[tagCategoryID].push(tagID);
        } else {
          output[tagCategoryID] = [tagID];
        }
      }

      return output;
    },
    setCustomerID (customerID) {
      this.filters.customer_id = customerID;
    }
  },
  beforeDestroy () {
    this.$bus.$off('ui-select-customer-updated', this.setCustomerID);
  }
};
</script>
